// The popover lives in @apsio/ui (APS-412); here it closes on the console's route changes.
//
// The bar's backdrop-filter makes it the containing block of the fixed popovers inside it, so the
// package's viewport coordinates landed offset by the bar's own place: at desktop width, a chip's
// menu opened a sidebar's width to the right of its chip (APS-508, Mosaic). Here the popover is
// placed under its button, left-aligned to it, or right-aligned when it would leave the window,
// and the layer's origin is measured and taken off.
import { usePopover as usePackagePopover } from '@apsio/ui'
import { useLocation } from '@tanstack/react-router'
import { type CSSProperties, useLayoutEffect, useState } from 'react'

export function usePopover(placement: 'below' | 'above' = 'below') {
  const popover = usePackagePopover(useLocation({ select: (l) => l.pathname }), placement)
  const { open, anchor, pop } = popover
  const [place, setPlace] = useState<CSSProperties | null>(null)

  useLayoutEffect(() => {
    if (!open) {
      setPlace(null)
      return
    }
    const measure = () => {
      const a = anchor.current?.getBoundingClientRect()
      const p = pop.current
      if (!a || !p) return
      // Where (0, 0) of the popover's layer is: at it, the box sits at the layer's origin.
      const { left: before, top: beforeTop } = p.style
      p.style.left = '0px'
      p.style.top = '0px'
      const origin = p.getBoundingClientRect()
      p.style.left = before
      p.style.top = beforeTop
      const w = p.offsetWidth
      const h = p.offsetHeight
      const fits = a.left + w <= window.innerWidth - 8
      const left = Math.max(8, fits ? a.left : a.right - w)
      const top = placement === 'below' ? a.bottom + 6 : Math.max(8, a.top - h - 8)
      setPlace({ left: left - origin.left, top: top - origin.top })
    }
    measure()
    window.addEventListener('resize', measure)
    return () => window.removeEventListener('resize', measure)
  }, [open, placement, anchor, pop])

  return { ...popover, style: open && place ? place : popover.style }
}
